import React, { useState } from 'react';
import styled, { createGlobalStyle } from 'styled-components';
import { FaCogs, FaClipboardList, FaRegLightbulb, FaExchangeAlt, FaQuestionCircle, FaPlus, FaMinus } from 'react-icons/fa';
import { MdDataUsage, MdOutlineStrategy, MdPeopleOutline, MdCheck } from 'react-icons/md';
import Home from '../Home';
import LinkedInPage2 from './LinkedInPage2';

// --- Global Styles ---
const GlobalStyle = createGlobalStyle`
  @import url('https://fonts.googleapis.com/css2?family=Montserrat:wght@400;600;700;800;900&display=swap');
  * {
    box-sizing: border-box;
  }
  body {
    margin: 0;
    padding: 0;
    font-family: 'Montserrat', sans-serif;
    background-color: #f5f5f5;
    color: #333;
    line-height: 1.6;
    overflow-x: hidden;
  }
`;

// --- Main Containers & Layout ---
const PageContainer = styled.div`
  //max-width: 1400px;
  margin: 0 auto;
  background-color: #fff;
  box-shadow: 0 0 10px rgba(0,0,0,0.1);
  
  @media (max-width: 1400px) {
    box-shadow: none;
  }
`;

const Section = styled.section`
  padding: 60px 5%;
  background-color: ${(props) => props.$bgColor || 'transparent'};
  color: ${(props) => props.$color || '#333'};
  
  @media (max-width: 768px) {
    padding: 40px 5%;
  }
`;

// --- Header Section ---
const HeaderSection = styled(Section)`
  background: linear-gradient(135deg, rgba(0, 119, 181, 0.9) 0%, rgba(0, 95, 145, 0.9) 100%), 
              url('https://picsum.photos/1400/450') no-repeat center center;
  background-size: cover;
  min-height: 400px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: flex-start;
  text-align: left;
  position: relative;
  padding: 100px 5% 60px;
  
  @media (max-width: 768px) {
    min-height: 300px;
    padding: 80px 5% 40px;
    text-align: center;
    align-items: center;
  }
`;

const HeaderContent = styled.div`
  position: relative;
  z-index: 10;
  max-width: 600px;
  color: white;
  
  @media (max-width: 768px) {
    max-width: 100%;
  }
`;

const LogoText = styled.div`
  position: absolute;
  top: 30px;
  right: 5%;
  z-index: 11;
  color: white;
  font-size: clamp(24px, 4vw, 30px);
  font-weight: 800;
  
  span {
    color: #ffd700;
  }
  
  @media (max-width: 768px) {
    position: relative;
    top: 0;
    right: 0;
    margin-bottom: 20px;
  }
`;

const BoostText = styled.h1`
  font-size: clamp(32px, 5vw, 48px);
  margin-bottom: 15px;
  line-height: 1.2;
  font-weight: 900;
  
  @media (max-width: 768px) {
    line-height: 1.3;
  }
`;

const BrandText = styled.span`
  color: #ffd700;
`;

const MarketingText = styled.span`
  color: #ffd700;
`;

const SubHeadline = styled.p`
  font-size: clamp(16px, 2.5vw, 18px);
  font-weight: 400;
  margin-bottom: 30px;
  line-height: 1.5;
`;

const GetAQuoteButton = styled.a`
  display: inline-block;
  background-color: #ffd700;
  color: #333;
  padding: 15px 35px;
  text-decoration: none;
  font-weight: 700;
  border-radius: 50px;
  font-size: 16px;
  transition: all 0.3s ease;
  cursor: pointer;
  border: 2px solid transparent;
  
  &:hover {
    background-color: transparent;
    border-color: #ffd700;
    color: #ffd700;
    transform: translateY(-2px);
    box-shadow: 0 5px 15px rgba(255, 215, 0, 0.3);
  }
  
  @media (max-width: 768px) {
    padding: 12px 25px;
    font-size: 14px;
  }
`;

// --- Service Section ---
const SectionTitle = styled.h2`
  font-size: clamp(28px, 4vw, 36px);
  font-weight: 700;
  color: #333;
  margin-bottom: 40px;
  padding: 15px 40px;
  display: inline-block;
  border: 2px solid #0077b5;
  border-radius: 50px;
  text-align: center;
  
  @media (max-width: 768px) {
    padding: 12px 25px;
    font-size: 24px;
    display: block;
    margin-left: auto;
    margin-right: auto;
    width: fit-content;
  }
`;

const ServiceGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 30px;
  margin-top: 50px;
  max-width: 1200px;
  margin: 50px auto 0;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 20px;
    margin-top: 30px;
  }
`;

const ServiceCard = styled.div`
  background: linear-gradient(145deg, #ffffff, #f8f9fa);
  padding: 30px;
  border-radius: 15px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.08);
  text-align: left;
  border-left: 5px solid #0077b5;
  transition: all 0.3s ease;
  height: 100%;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  
  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 15px 40px rgba(0, 119, 181, 0.15);
  }
  
  @media (max-width: 768px) {
    padding: 20px;
    margin: 0 10px;
  }
`;

const ServiceTitle = styled.h3`
  font-size: clamp(18px, 2.5vw, 20px);
  font-weight: 700;
  color: #0077b5;
  margin-top: 0;
  margin-bottom: 15px;
  line-height: 1.3;
`;

const ServiceDescription = styled.p`
  font-size: clamp(14px, 2vw, 15px);
  color: #666;
  margin: 0;
  line-height: 1.6;
`;

// --- Marketing Foundations Section ---
const FoundationsSection = styled(Section)`
  background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
  padding-bottom: 60px;
`;

const FoundationsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 25px;
  margin: 40px auto 0;
  max-width: 1200px;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 20px;
  }
`;

const FoundationItem = styled.div`
  background: white;
  padding: 25px;
  border-radius: 12px;
  box-shadow: 0 5px 20px rgba(0, 0, 0, 0.06);
  min-height: 200px;
  text-align: center;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: center;
  position: relative;
  transition: all 0.3s ease;
  
  &:hover {
    transform: translateY(-3px);
    box-shadow: 0 8px 25px rgba(0, 0, 0, 0.1);
  }
  
  &:after {
    content: '';
    position: absolute;
    top: 0;
    left: 50%;
    transform: translateX(-50%);
    width: 80%;
    height: 4px;
    background: linear-gradient(90deg, #0077b5, #00a0dc);
    border-radius: 0 0 2px 2px;
  }
`;

const FoundationIcon = styled.div`
  width: 60px;
  height: 60px;
  border-radius: 50%;
  background: linear-gradient(135deg, #0077b5, #00a0dc);
  margin: 0 auto 15px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 24px;
  font-weight: bold;
`;

const FoundationTitle = styled.h4`
  font-size: clamp(16px, 2.5vw, 18px);
  font-weight: 700;
  color: #333;
  margin: 10px 0 10px;
`;

const FoundationDescription = styled.p`
  font-size: clamp(13px, 2vw, 14px);
  color: #666;
  margin: 0;
  line-height: 1.5;
`;

const LetSTalkButton = styled(GetAQuoteButton)`
  background-color: #0077b5;
  color: white;
  margin-top: 40px;
  
  &:hover {
    background-color: transparent;
    border-color: #0077b5;
    color: #0077b5;
  }
`;

// --- Lead Generation Section ---
const LeadGenSection = styled(Section)`
  padding-top: 60px;
`;

const LeadGenTitle = styled.h2`
  font-size: clamp(20px, 3vw, 24px);
  font-weight: 600;
  color: #333;
  margin-bottom: 20px;
  text-align: center;
`;

const LeadGenBlueprint = styled.div`
  max-width: 900px;
  margin: 0 auto;
`;

const BlueprintItem = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  text-align: left;
  margin-bottom: 25px;
  padding: 20px;
  border-radius: 10px;
  background: #f8f9fa;
  transition: all 0.3s ease;
  
  &:hover {
    background: #e9ecef;
    transform: translateX(5px);
  }
  
  @media (max-width: 768px) {
    flex-direction: column;
    text-align: center;
    padding: 15px;
  }
`;

const BlueprintText = styled.div`
  flex-grow: 1;
  max-width: 70%;
  padding-right: 20px;
  
  @media (max-width: 768px) {
    max-width: 100%;
    padding-right: 0;
    margin-bottom: 15px;
  }
`;

const BlueprintTitle = styled.h4`
  font-size: clamp(16px, 2.5vw, 18px);
  font-weight: 700;
  color: #333;
  margin: 0 0 8px;
`;

const BlueprintDescription = styled.p`
  font-size: clamp(13px, 2vw, 14px);
  color: #666;
  margin: 0;
  line-height: 1.5;
`;

const BlueprintIcon = styled.div`
  width: 60px;
  height: 60px;
  border-radius: 50%;
  border: 2px solid #0077b5;
  color: #0077b5;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 24px;
  font-weight: bold;
  background: rgba(0, 119, 181, 0.1);
  flex-shrink: 0;
  
  @media (max-width: 768px) {
    width: 50px;
    height: 50px;
    font-size: 20px;
  }
`;

// --- Unlocking LinkedIn Section ---
const LinkedInHeader = styled.div`
  text-align: center;
  margin-bottom: 40px;
`;

const LinkedInTitle = styled.h2`
  font-size: clamp(28px, 4vw, 32px);
  font-weight: 600;
  color: #333;
  margin-bottom: 15px;
`;

const LinkedInSubtitle = styled.p`
  font-size: clamp(15px, 2.5vw, 16px);
  color: #666;
  max-width: 900px;
  margin: 0 auto;
  line-height: 1.6;
`;

const StepContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 30px;
  padding: 20px 0;
  max-width: 1000px;
  margin: 0 auto;
`;

const StepItem = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 25px;
  border-bottom: 1px solid #eee;
  padding-bottom: 30px;
  
  &:last-child {
    border-bottom: none;
    padding-bottom: 0;
  }
  
  @media (max-width: 768px) {
    flex-direction: column;
    text-align: center;
    gap: 15px;
  }
`;

const StepNumberCircle = styled.div`
  width: 50px;
  height: 50px;
  min-width: 50px;
  border: 3px solid #0077b5;
  border-radius: 50%;
  display: flex;
  justify-content: center;
  align-items: center;
  font-size: 20px;
  font-weight: 700;
  color: #0077b5;
  background: rgba(0, 119, 181, 0.1);
  
  @media (max-width: 768px) {
    width: 40px;
    height: 40px;
    font-size: 18px;
    align-self: center;
  }
`;

const StepContent = styled.div`
  flex: 1;
`;

const StepTitle = styled.h3`
  font-size: clamp(18px, 2.5vw, 20px);
  font-weight: 700;
  color: #333;
  margin-bottom: 10px;
`;

const StepDescription = styled.p`
  font-size: clamp(14px, 2.5vw, 15px);
  color: #666;
  line-height: 1.6;
`;

// --- Why Choose Us Section ---
const WhyChooseUsSection = styled(Section)`
  background: linear-gradient(135deg, #2c3e50 0%, #34495e 100%);
  color: #fff;
  text-align: center;
  padding: 80px 5%;
`;

const ChooseUsTitle = styled.h2`
  font-size: clamp(28px, 4vw, 32px);
  font-weight: 400;
  margin-bottom: 60px;
`;

const CardGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 30px;
  margin-bottom: 40px;
`;

const Card = styled.div`
  background: rgba(255, 255, 255, 0.08);
  padding: 40px 25px;
  border-radius: 15px;
  text-align: center;
  min-height: 280px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  position: relative;
  transition: all 0.3s ease;
  backdrop-filter: blur(10px);
  
  &:hover {
    background: rgba(255, 255, 255, 0.12);
    transform: translateY(-5px);
  }
`;

const CardIcon = styled.div`
  font-size: 48px;
  color: #ffd700;
  margin-bottom: 20px;
`;

const CardTitle = styled.h4`
  font-size: clamp(18px, 2.5vw, 20px);
  font-weight: 700;
  margin-bottom: 15px;
  color: #fff;
`;

const CardDescription = styled.p`
  font-size: clamp(14px, 2.5vw, 15px);
  color: #ecf0f1;
  line-height: 1.6;
`;

const SubCardGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 25px;
`;

const SubCard = styled(Card)`
  background: rgba(0, 0, 0, 0.15);
  padding: 30px 20px;
  min-height: 220px;
`;

// --- Enhanced FAQ Section ---
const FAQSection = styled(Section)`
  padding: 60px 5%;
  background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
`;

const FAQTitle = styled.h2`
  font-size: clamp(28px, 4vw, 32px);
  font-weight: 700;
  text-align: center;
  margin-bottom: 40px;
  color: #333;
`;

const FAQContainer = styled.div`
  max-width: 1000px;
  margin: 0 auto;
`;

const FAQItemWrapper = styled.div`
  margin-bottom: 15px;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.08);
  transition: all 0.3s ease;
  
  &:hover {
    box-shadow: 0 6px 20px rgba(0, 119, 181, 0.15);
  }
`;

const FAQHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px 25px;
  background: white;
  cursor: pointer;
  transition: all 0.3s ease;
  
  &:hover {
    background: #f8f9fa;
  }
`;

const FAQQuestion = styled.p`
  font-size: clamp(15px, 2.5vw, 16px);
  color: #333;
  display: flex;
  align-items: center;
  margin: 0;
  font-weight: 600;
  flex: 1;
`;

const FAQIcon = styled.span`
  margin-right: 15px;
  color: #0077b5;
  font-size: 18px;
  display: flex;
  align-items: center;
`;

const FAQToggleIcon = styled.span`
  font-size: 18px;
  color: #0077b5;
  font-weight: bold;
  transition: transform 0.3s ease;
  transform: ${props => props.$isOpen ? 'rotate(180deg)' : 'rotate(0deg)'};
  display: flex;
  align-items: center;
`;

const FAQAnswer = styled.div`
  padding: ${props => props.$isOpen ? '25px' : '0 25px'};
  background: white;
  border-top: ${props => props.$isOpen ? '1px solid #e9ecef' : 'none'};
  max-height: ${props => props.$isOpen ? '500px' : '0'};
  opacity: ${props => props.$isOpen ? '1' : '0'};
  overflow: hidden;
  transition: all 0.4s ease;
  font-size: clamp(14px, 2.5vw, 15px);
  color: #666;
  line-height: 1.7;
  
  p {
    margin: 0 0 15px 0;
    
    &:last-child {
      margin-bottom: 0;
    }
  }
  
  ul {
    margin: 10px 0;
    padding-left: 20px;
    
    li {
      margin-bottom: 8px;
      position: relative;
      
      &:before {
        content: '•';
        color: #0077b5;
        font-weight: bold;
        margin-right: 10px;
      }
    }
  }
`;

// --- Footer ---
const FooterBar = styled.div`
  background: linear-gradient(135deg, #2c3e50 0%, #34495e 100%);
  color: #fff;
  padding: 40px 5%;
  text-align: center;
`;

const FollowUsText = styled.p`
  font-size: clamp(14px, 2.5vw, 16px);
  margin-bottom: 15px;
  font-weight: 300;
`;

const SocialLinksText = styled.p`
  font-size: clamp(15px, 2.5vw, 18px);
  margin-bottom: 25px;
  font-weight: 600;
`;

const ButtonContainer = styled.div`
  display: flex;
  justify-content: center;
  gap: 15px;
  flex-wrap: wrap;
`;

const SocialButton = styled.a`
  display: inline-block;
  padding: 12px 25px;
  background-color: #ffd700;
  color: #2c3e50;
  text-decoration: none;
  border-radius: 50px;
  font-weight: 700;
  font-size: 14px;
  transition: all 0.3s ease;
  border: 2px solid transparent;
  
  &:hover {
    background-color: transparent;
    border-color: #ffd700;
    color: #ffd700;
    transform: translateY(-2px);
  }
`;

// --- Data ---
const linkedinSteps = [
    { number: 1, title: "Profile Power-Up", description: "Optimize your LinkedIn profile to be SEO-friendly and to showcase not only your skills and experience but also your unique personality. A well-crafted profile captures attention, making visitors want to know more about SRK Software." },
    { number: 2, title: "Engaging Content Strategy", description: "Develop a content calendar that includes informative articles, engaging visual posts, and thought-provoking discussions. Consistently share valuable insights helps you stay on your audience's radar and encourages meaningful interactions." },
    { number: 3, title: "Strategic Networking", description: "Engage authentically with connections by commenting on their posts, sharing insights, and attending virtual events. Focus on quality connections over quantity to build strong, lasting relationships." },
    { number: 4, title: "Thought Leadership Sharing", description: "Share original articles, comment on industry news, and re-share well-researched articles. Highlight pressing perspectives, and actionable tips. Consistently providing value builds credibility and attracts a loyal following." },
    { number: 5, title: "Connection Outreach", description: "When reaching out to potential leads, take the time to personalize your messages. Highlight shared interests, common connections, or recent accomplishments to make a memorable first impression." },
    { number: 6, title: "Data-Driven Refinement", description: "Regularly analyze your LinkedIn insights to understand what's resonating with your audience. Adjust your strategy based on engagement metrics and keyword performance to maximize your brand's advantage." },
];

const mainCards = [
    { icon: FaCogs, title: "Cutting-Edge Technology", description: "Our innovative solutions harness the power of artificial intelligence, automation and analytics. SRK Software deploys the latest digital strategies to thrive in today's dynamic landscape." },
    { icon: MdPeopleOutline, title: "Tailored for You", description: "We understand that every business is unique. Our solutions are custom-built to perfectly align with your specific goals, challenges, and target audience. Your success truly drives our strategy." },
    { icon: FaClipboardList, title: "Expertise That Matters", description: "Our marketing team is with a depth of industry experience across diverse sectors. We leverage this knowledge to bring valuable insights to the table, ensuring strategies that benchmark and deliver." },
];

const subCards = [
    { icon: MdDataUsage, title: "Data-Driven Strategies", description: "Every move we make is backed by rigorous data analysis. We don't rely on guesswork; we develop precise, actionable craft strategies that are not just trendy, but are empirically proven leading to meaningful results." },
    { icon: FaRegLightbulb, title: "Holistic Approach", description: "We don't just focus on one base of your digital presence. SRK Software integrates everything seamlessly - from content creation and engagement to comprehensive analytics, ensuring every aspect is optimized." },
    { icon: FaExchangeAlt, title: "Transparency and Collaboration", description: "We maintain clear and transparent communication throughout every project. SRK Software partners with you every step of the way, continuously ensuring a shared vision and a strong, successful relationship." },
];

const faqData = [
  {
    question: "What is LinkedIn Marketing, and why is it important for businesses?",
    answer: "LinkedIn Marketing is a strategic approach to promoting your business, products, or services on the LinkedIn platform. It's crucial for B2B companies because LinkedIn is the world's largest professional network with over 900 million members. It allows you to reach decision-makers, build brand authority, generate high-quality leads, and establish thought leadership in your industry."
  },
  {
    question: "How can I measure the success of my LinkedIn Marketing efforts?",
    answer: "You can measure success through various metrics including profile views, connection growth, engagement rates (likes, comments, shares), click-through rates on content, lead generation form completions, and ultimately, conversion rates. We provide comprehensive analytics dashboards that track these KPIs and show ROI clearly."
  },
  {
    question: "How can SRK Software help me with my LinkedIn Marketing?",
    answer: "SRK Software provides end-to-end LinkedIn marketing solutions including profile optimization, content strategy development, targeted advertising campaigns, engagement management, and performance analytics. Our team of experts creates customized strategies aligned with your business goals and target audience."
  },
  {
    question: "Do I need a LinkedIn Company page to engage in LinkedIn Marketing?",
    answer: "While having a Company page is highly recommended for businesses, you can start with an optimized personal profile. However, a Company page provides additional features like showcasing your products/services, sharing company updates, and running targeted ads. We help you set up and optimize both personal profiles and Company pages."
  },
  {
    question: "What types of content work best for LinkedIn Marketing?",
    answer: "The most effective content types on LinkedIn include: <ul><li>Thought leadership articles and posts</li><li>Industry insights and trends</li><li>Case studies and success stories</li><li>Professional tips and how-to guides</li><li>Company news and updates</li><li>Video content and webinars</li><li>Infographics and data visualizations</li></ul>"
  },
  {
    question: "Is LinkedIn Marketing suitable for all industries and business sizes?",
    answer: "LinkedIn Marketing is particularly effective for B2B companies, professional services, tech industries, and any business targeting professionals or decision-makers. While it works for businesses of all sizes, the strategy differs. Small businesses might focus on personal branding and networking, while larger enterprises may leverage Company pages and sophisticated ad campaigns."
  },
  {
    question: "How often should I update my LinkedIn profile for marketing purposes?",
    answer: "You should review and update your LinkedIn profile at least quarterly. Regular updates should include recent achievements, new skills, updated portfolio pieces, and fresh recommendations. For active marketing, we recommend posting valuable content 3-5 times per week and engaging with your network daily for optimal results."
  },
  {
    question: "Can LinkedIn Marketing be integrated with other marketing channels?",
    answer: "Absolutely! LinkedIn Marketing works best when integrated with your overall marketing strategy. It can be seamlessly combined with email marketing, content marketing, SEO, and other social media platforms. We help create cohesive cross-channel campaigns that amplify your message and drive consistent results across all touchpoints."
  }
];

// --- The Component ---
const LinkedInMarketing = () => {
  const [expandedFaq, setExpandedFaq] = useState(null);

  const toggleFaq = (index) => {
    setExpandedFaq(expandedFaq === index ? null : index);
  };

  return (
    <Home>
      <GlobalStyle />
      <PageContainer>
        {/* Header Section */}
        <HeaderSection>
          <LogoText>
            SRK Software <span>in</span>
          </LogoText>
          <HeaderContent>
            <BoostText>
              Boost Your <BrandText>Brand's</BrandText> Presence with <MarketingText>LinkedIn</MarketingText> Marketing!
            </BoostText>
            <SubHeadline>
              Experience the pinnacle of professional networking and B2B growth with our bespoke marketing strategies & solutions.
            </SubHeadline>
            <GetAQuoteButton href="#contact">GET A FREE QUOTE</GetAQuoteButton>
          </HeaderContent>
        </HeaderSection>

        {/* Our Service Section */}
        <Section>
          <SectionTitle>Our Service</SectionTitle>
          <ServiceGrid>
            <ServiceCard>
              <ServiceTitle>Strategic Profile Enhancement</ServiceTitle>
              <ServiceDescription>
                Enhance your expert online presence with custom optimizations to align with your brand's core strengths. We optimize your profile, content, recommendations, and expertise, ensuring you stand out to your target audience.
              </ServiceDescription>
            </ServiceCard>
            <ServiceCard>
              <ServiceTitle>Engagement-Driven Content Creation</ServiceTitle>
              <ServiceDescription>
                Let us craft your compelling narratives. Our experts develop relevant, insightful, and shareable content that reinforces your reputation as a thought leader. We strategically time and format all content to ensure your audience is engaged and eager to connect.
              </ServiceDescription>
            </ServiceCard>
            <ServiceCard>
              <ServiceTitle>Precision Targeting with LinkedIn Ads</ServiceTitle>
              <ServiceDescription>
                Make every ad dollar count. Showing your highly engaging content to the right audience is key. Our ads are seen by decision-makers, executives, and relevant professionals, ensuring a high probability of meaningful conversations and partnerships.
              </ServiceDescription>
            </ServiceCard>
          </ServiceGrid>
        </Section>

        {/* LinkedIn's Marketing Foundations Section */}
        <FoundationsSection>
          <SectionTitle>LinkedIn's Marketing Foundations</SectionTitle>
          <FoundationsGrid>
            <FoundationItem>
              <FoundationIcon>🧑‍💻</FoundationIcon>
              <FoundationTitle>Profile Brilliance</FoundationTitle>
              <FoundationDescription>
                Craft an immaculate online presence that leaves a lasting impression on connections.
              </FoundationDescription>
            </FoundationItem>
            <FoundationItem>
              <FoundationIcon>✍️</FoundationIcon>
              <FoundationTitle>Engaging Content Strategy</FoundationTitle>
              <FoundationDescription>
                Share valuable insights, latest trends, and industry news to nurture new, quality conversations.
              </FoundationDescription>
            </FoundationItem>
            <FoundationItem>
              <FoundationIcon>🔗</FoundationIcon>
              <FoundationTitle>Network Expansion</FoundationTitle>
              <FoundationDescription>
                Systematically grow your connections, connecting with peers, influencers, and potential clients.
              </FoundationDescription>
            </FoundationItem>
            <FoundationItem>
              <FoundationIcon>💖</FoundationIcon>
              <FoundationTitle>Relationship Nurturing</FoundationTitle>
              <FoundationDescription>
                Engage authentically with your connections through insightful messaging and follow-ups.
              </FoundationDescription>
            </FoundationItem>
            <FoundationItem>
              <FoundationIcon>👥</FoundationIcon>
              <FoundationTitle>Strategic Group Participation</FoundationTitle>
              <FoundationDescription>
                Join and actively contribute to LinkedIn groups to connect with like-minded individuals.
              </FoundationDescription>
            </FoundationItem>
            <FoundationItem>
              <FoundationIcon>📊</FoundationIcon>
              <FoundationTitle>Data-Driven Optimization</FoundationTitle>
              <FoundationDescription>
                Analyze key performance metrics, track key results, and refine your strategies based on insights.
              </FoundationDescription>
            </FoundationItem>
          </FoundationsGrid>
          <div style={{ textAlign: 'center' }}>
            <LetSTalkButton href="#contact">LET'S TALK</LetSTalkButton>
          </div>
        </FoundationsSection>

        {/* Lead Generation Blueprint Section */}
        <LeadGenSection>
          <LeadGenTitle>Lead Generation Blueprint</LeadGenTitle>
          <p style={{ fontSize: 'clamp(14px, 2.5vw, 16px)', color: '#666', marginBottom: '50px', textAlign: 'center' }}>
            Let's see how we do the LinkedIn marketing to make it more effective
          </p>
          <LeadGenBlueprint>
            {[
              { title: "Targeted Audience Profiling", description: "Identify your ideal client profiles, complete with demographics, interests, and behaviour.", icon: "🎯" },
              { title: "Webinars and Workshops", description: "Host online events to provide value, showcase your expertise, capture leads, and foster relationships with attendees.", icon: "🔴" },
              { title: "Compelling Content Marketing on LinkedIn", description: "Create and share high-quality posts, articles, and documents that educate and convert.", icon: "💬" },
              { title: "Referral Programs", description: "Incentivize your existing network to refer others in exchange for rewards, tapping into their networks for new business.", icon: "🎁" },
              { title: "High-Converting Landing Pages", description: "Design landing pages that resonate with your leads and encourages them to share their contact information.", icon: "🧲" },
              { title: "Interactive Quizzes and Surveys", description: "Develop engaging, fun, and informative quizzes, collecting data that helps tailor follow-up content to user needs.", icon: "🧩" },
              { title: "Engaging Social Media Campaigns", description: "Execute highly targeted paid campaigns to drive conversions, drive engagement, and direct relevant traffic to your landing pages.", icon: "📱" },
              { title: "Influencer Collaborations", description: "Partner with key industry thought leaders and influencers, expanding your reach and credibility among their highly engaged audience.", icon: "💰" },
              { title: "Email Marketing Funnels", description: "Implement a drip-feed email sequence for consistently delivering relevant content and offers that guide them toward conversion.", icon: "📧" },
              { title: "SEO and Content Optimization", description: "Optimize all your LinkedIn and external content, making it easier for potential leads to find you when they search for solutions.", icon: "🔎" },
            ].map((item, index) => (
              <BlueprintItem key={index}>
                <BlueprintText>
                  <BlueprintTitle>{item.title}</BlueprintTitle>
                  <BlueprintDescription>{item.description}</BlueprintDescription>
                </BlueprintText>
                <BlueprintIcon>{item.icon}</BlueprintIcon>
              </BlueprintItem>
            ))}
          </LeadGenBlueprint>
        </LeadGenSection>

        {/* Unlocking LinkedIn Section */}
        <Section>
          <LinkedInHeader>
            <LinkedInTitle>Unlocking LinkedIn's Potential</LinkedInTitle>
            <LinkedInSubtitle>
              A step-by-step guide to transforming your LinkedIn presence into a powerful marketing engine
            </LinkedInSubtitle>
          </LinkedInHeader>
          <StepContainer>
            {linkedinSteps.map((step, index) => (
              <StepItem key={index}>
                <StepNumberCircle>{step.number}</StepNumberCircle>
                <StepContent>
                  <StepTitle>{step.title}</StepTitle>
                  <StepDescription>{step.description}</StepDescription>
                </StepContent>
              </StepItem>
            ))}
          </StepContainer>
        </Section>

        {/* Why Choose Us Section */}
        <WhyChooseUsSection>
          <ChooseUsTitle>Why Choose SRK Software?</ChooseUsTitle>
          <CardGrid>
            {mainCards.map((card, index) => (
              <Card key={index}>
                <CardIcon>
                  <card.icon />
                </CardIcon>
                <CardTitle>{card.title}</CardTitle>
                <CardDescription>{card.description}</CardDescription>
              </Card>
            ))}
          </CardGrid>
          <SubCardGrid>
            {subCards.map((card, index) => (
              <SubCard key={index}>
                <CardIcon>
                  <card.icon />
                </CardIcon>
                <CardTitle>{card.title}</CardTitle>
                <CardDescription>{card.description}</CardDescription>
              </SubCard>
            ))}
          </SubCardGrid>
        </WhyChooseUsSection>

        {/* Enhanced FAQ Section */}
        <FAQSection>
          <FAQTitle>Frequently Asked Questions</FAQTitle>
          <FAQContainer>
            {faqData.map((faq, index) => (
              <FAQItemWrapper key={index}>
                <FAQHeader onClick={() => toggleFaq(index)}>
                  <FAQQuestion>
                    <FAQIcon><FaQuestionCircle /></FAQIcon>
                    {faq.question}
                  </FAQQuestion>
                  <FAQToggleIcon $isOpen={expandedFaq === index}>
                 
                  </FAQToggleIcon>
                </FAQHeader>
                <FAQAnswer 
                  $isOpen={expandedFaq === index}
                  dangerouslySetInnerHTML={{ __html: faq.answer }}
                />
              </FAQItemWrapper>
            ))}
          </FAQContainer>
        </FAQSection>

        {/* Footer */}
        <FooterBar>
          <FollowUsText>Follow us on social media for more updates</FollowUsText>
          <SocialLinksText>Connect With Us</SocialLinksText>
          <ButtonContainer>
            <SocialButton href="#">LinkedIn</SocialButton>
            <SocialButton href="#">Twitter</SocialButton>
            <SocialButton href="#">Facebook</SocialButton>
            <SocialButton href="#">Instagram</SocialButton>
          </ButtonContainer>
        </FooterBar>

    {/*     <LinkedInPage2 /> */}
      </PageContainer>
    </Home>
  );
};

export default LinkedInMarketing;